import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  fullscreenFeaturePreloads = new Set();
  fullscreenHydrationRetryQueued = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    if (this.#isFullscreenDrawer()) {
      requestAnimationFrame(() => {
        this.#hydrateFullscreenFeatures();
        this.#preloadFullscreenFeatureImages();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle direct click interactions inside the drawer.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const mobileBackTrigger = event.target.closest('[data-fullscreen-mobile-back]');

    if (mobileBackTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#returnToFullscreenMainMenu();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    event?.preventDefault?.();

    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    if (this.#isFullscreenDrawer()) {
      this.#setFullscreenScrollbarOffset();
      details.classList.add('menu-open');
      this.#hydrateFullscreenFeatures();
      this.#preloadFullscreenFeatureImages();
      this.#initializeFullscreenMenu();
      trapFocus(details);
      return;
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      this.#initializeFullscreenMenu();

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (this.#isFullscreenDrawer() && this.refs.menuDrawer.dataset.hasActive === 'true') {
      event?.preventDefault?.();
      this.#returnToFullscreenMainMenu();
      return;
    }

    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Activate a fullscreen menu item.
   * @param {Event} event
   */
  activateFullscreenItem(event) {
    if (this.refs.menuDrawer.dataset.suppressActivation === 'true') {
      event?.preventDefault?.();
      return;
    }

    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-fullscreen-trigger]');

    if (!(trigger instanceof HTMLElement)) return;

    if (this.#isMobileFullscreenLayout()) {
      event.preventDefault();
    }

    const key = trigger.dataset.menuItemKey;

    if (!key) return;

    this.#setFullscreenActiveItem(key);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    delete this.refs.menuDrawer.dataset.activeKey;

    if (this.#isFullscreenDrawer()) {
      this.#clearFullscreenActiveItem();
      this.#clearFullscreenScrollbarOffset();
      reset(details);
      removeTrapFocus();
      return;
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #isFullscreenDrawer() {
    return this.refs.menuDrawer.classList.contains('menu-drawer--fullscreen');
  }

  #initializeFullscreenMenu() {
    if (!this.refs.menuDrawer.classList.contains('menu-drawer--fullscreen')) return;

    if (this.#isMobileFullscreenLayout()) {
      this.#clearFullscreenActiveItem();
      return;
    }

    const defaultTrigger =
      this.querySelector('[data-fullscreen-trigger][data-current-active]') ||
      this.querySelector('[data-fullscreen-trigger][data-default-active]') ||
      this.querySelector('[data-fullscreen-trigger]');

    if (!(defaultTrigger instanceof HTMLElement)) return;

    const key = defaultTrigger.dataset.menuItemKey;

    if (!key) return;

    this.#setFullscreenActiveItem(key);
  }

  #hydrateFullscreenFeatures() {
    const templates = new Map();

    this.querySelectorAll('.header-menu__media-template[data-menu-item-key]').forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;

      const key = template.dataset.menuItemKey?.trim();

      if (!key) return;

      templates.set(key, template);
    });

    if (templates.size === 0) {
      this.#queueFullscreenHydrationRetry();
      return;
    }

    this.querySelectorAll('[data-fullscreen-feature]').forEach((slot) => {
      if (!(slot instanceof HTMLElement)) return;
      if (slot.dataset.hydrated === 'true') return;

      const key = slot.dataset.menuItemKey;
      const template = key ? templates.get(key) : null;

      if (!template) return;

      const firstItem = template.content.querySelector('.mega-menu__media-item');

      if (!firstItem) return;

      const clonedItem = firstItem.cloneNode(true);
      clonedItem.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'high';
        this.#preloadImage(image);
      });

      slot.replaceChildren(clonedItem);
      slot.dataset.hydrated = 'true';
    });
  }

  #queueFullscreenHydrationRetry() {
    if (this.fullscreenHydrationRetryQueued) return;

    this.fullscreenHydrationRetryQueued = true;
    requestAnimationFrame(() => {
      this.fullscreenHydrationRetryQueued = false;
      this.#hydrateFullscreenFeatures();
      this.#preloadFullscreenFeatureImages();
    });
  }

  #preloadFullscreenFeatureImages() {
    this.querySelectorAll('[data-fullscreen-feature] img, .header-menu__media-template img').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      this.#preloadImage(image);
    });
  }

  #preloadImage(image) {
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloadImageSource({
      src: image.currentSrc || image.src,
      srcset: image.srcset,
      sizes: image.sizes,
    });
  }

  #preloadImageSource(source) {
    const src = typeof source === 'string' ? source : source?.src;

    if (!src || this.fullscreenFeaturePreloads.has(src)) return;

    this.fullscreenFeaturePreloads.add(src);
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    if (typeof source !== 'string') {
      if (source.sizes) image.sizes = source.sizes;
      if (source.srcset) image.srcset = source.srcset;
    }
    image.src = src;
  }

  #returnToFullscreenMainMenu() {
    if (!this.#isFullscreenDrawer()) return;

    this.refs.menuDrawer.dataset.suppressActivation = 'true';
    this.#clearFullscreenActiveItem();
    this.refs.menuDrawer.scrollTo({ top: 0, left: 0 });

    requestAnimationFrame(() => {
      delete this.refs.menuDrawer.dataset.suppressActivation;
    });
  }

  #setFullscreenScrollbarOffset() {
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    this.refs.menuDrawer.style.setProperty('--fullscreen-menu-scrollbar-width', `${scrollbarWidth}px`);
  }

  #clearFullscreenScrollbarOffset() {
    this.refs.menuDrawer.style.removeProperty('--fullscreen-menu-scrollbar-width');
  }

  #isMobileFullscreenLayout() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  #clearFullscreenActiveItem() {
    delete this.refs.menuDrawer.dataset.activeKey;
    this.refs.menuDrawer.dataset.hasActive = 'false';

    this.querySelectorAll('[data-fullscreen-trigger]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      element.classList.remove('is-active');
      element.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll('[data-fullscreen-panel], [data-fullscreen-feature]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      element.hidden = true;
    });
  }

  /**
   * @param {string} key
   */
  #setFullscreenActiveItem(key) {
    if (this.refs.menuDrawer.dataset.activeKey === key) return;

    this.refs.menuDrawer.dataset.activeKey = key;
    this.refs.menuDrawer.dataset.hasActive = 'true';
    this.refs.menuDrawer.scrollTo({ top: 0, left: 0 });

    this.querySelectorAll('[data-fullscreen-trigger]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const isActive = element.dataset.menuItemKey === key;
      element.classList.toggle('is-active', isActive);
      element.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    this.querySelectorAll('[data-fullscreen-panel], [data-fullscreen-feature]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      element.hidden = element.dataset.menuItemKey !== key;
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
